import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaSearch,
  FaTimes,
  FaUserCircle,
  FaEye,
  FaEdit,
  FaCamera,
  FaBuilding,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ChevronDown, UserCheck } from "lucide-react";
import SummaryApi from "../common";

const AllOrganizationCustomers = () => {
  const [search, setSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const navigate = useNavigate();

  const BACKEND_BASE_URL = SummaryApi?.productImage?.url
    ? SummaryApi.productImage.url.replace("/api/uploads", "")
    : "http://192.168.1.144:3001";

  // --- 1. Fetch Data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch Users
      const userRes = await fetch(SummaryApi.organizationUsers.url, {
        method: SummaryApi.organizationUsers.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer oakyaecom_no_units_916",
        },
      });
      const userData = await userRes.json();

      // Fetch Organizations
      const orgRes = await fetch(SummaryApi.getActiveOrganization.url, {
        method: SummaryApi.getActiveOrganization.method,
      });
      const orgData = await orgRes.json();

      if (userData?.status) setAllUsers(userData.data || []);
      if (orgData?.status) setOrganizations(orgData.data || []);
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error("Failed to sync data with server");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- 2. Filter & Pagination Logic ---
  const filteredUsers = useMemo(() => {
    return allUsers.filter((user) => {
      const matchesSearch =
        `${user?.user_name || ""} ${user?.email || ""} ${user?.mobile_number || ""}`
          .toLowerCase()
          .includes(search.toLowerCase());

      const matchesOrg =
        selectedOrgId === "" ||
        String(user?.organization_id) === String(selectedOrgId);

      return matchesSearch && matchesOrg;
    });
  }, [allUsers, search, selectedOrgId]);

  // Reset to page 1 when search or org filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedOrgId]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * itemsPerPage;
    const lastPageIndex = firstPageIndex + itemsPerPage;
    return filteredUsers.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredUsers]);

  // --- 3. Custom Success Toast ---
  const showSuccessToast = (title, description) => {
    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-md w-full bg-white shadow-2xl rounded-[2rem] pointer-events-auto flex ring-1 ring-black ring-opacity-5 p-4 border-b-8 border-pink-500`}
        >
          <div className="flex-1 w-0 p-2">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="h-12 w-12 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-600">
                  <UserCheck size={28} />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-lg font-black text-gray-900 uppercase tracking-tight leading-none">
                  {title}
                </p>
                <p className="mt-1 text-sm font-bold text-gray-500 uppercase tracking-wide">
                  {description}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-100">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-pink-600 hover:text-pink-500 focus:outline-none"
            >
              Close
            </button>
          </div>
        </motion.div>
      ),
      { duration: 3000 },
    );
  };

  // --- 4. Sub-components ---

  const CustomerCard = ({ user }) => {
    const profileImage = user.profile_image
      ? `${BACKEND_BASE_URL}/${user.profile_image}`
      : null;
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="group bg-white rounded-[2.5rem] shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border border-pink-50 relative"
      >
        <button
          onClick={() => {
            setEditUser(user);
            setModalOpen(true);
          }}
          className="absolute top-4 right-4 z-10 p-3 bg-white/80 backdrop-blur-md rounded-2xl shadow-lg hover:bg-pink-500 hover:text-white text-pink-600 transition-all"
        >
          <FaEdit size={18} />
        </button>

        <div className="h-48 bg-gradient-to-br from-pink-50 to-rose-100 relative overflow-hidden">
          {profileImage ? (
            <img
              src={profileImage}
              alt=""
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center opacity-20">
              <FaUserCircle size={100} />
            </div>
          )}
          <div className="absolute bottom-4 left-4">
            <span
              className={`px-4 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${user.is_active === 1 ? "bg-green-500 text-white" : "bg-gray-400 text-white"}`}
            >
              {user.is_active === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3 truncate">
            {user.user_name || "No Name"}
          </h3>

          <div className="space-y-2.5 text-sm text-gray-700">
            <p>
              <span className="font-semibold">Email:</span>{" "}
              {user.email || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Mobile:</span>{" "}
              {user.mobile_number || "N/A"}
            </p>
            <p>
              <span className="font-semibold">City:</span> {user.city || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Joined:</span>{" "}
              {formatDate(user.created_date)}
            </p>
          </div>

          <button
            onClick={() =>
              navigate(`/admin-panel/personaldashboard/${btoa(user.user_id)}`)
            }
            className="mt-6 w-full py-3.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-2xl hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg flex items-center justify-center gap-2.5 text-base"
          >
            Open Dashboard
          </button>
        </div>
      </motion.div>
    );
  };

  const EditCustomerModal = () => {
    const [formData, setFormData] = useState({
      ...editUser,
      is_active: editUser?.is_active === 1,
    });
    const [preview, setPreview] = useState(
      editUser?.profile_image
        ? `${BACKEND_BASE_URL}/${editUser.profile_image}`
        : null,
    );
    const [imgFile, setImgFile] = useState(null);

    const handleSubmit = async (e) => {
      e.preventDefault();
      const payload = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "is_active") payload.append(key, formData[key] ? "1" : "0");
        else payload.append(key, formData[key] || "");
      });
      if (imgFile) payload.append("profile_image", imgFile);

      try {
        const res = await fetch(SummaryApi.updateUser.url, {
          method: SummaryApi.updateUser.method,
          headers: { Authorization: "Bearer oakyaecom_no_units_916" },
          body: payload,
        });
        const result = await res.json();
        if (result.status) {
          showSuccessToast(
            "Profile Updated",
            `${formData.user_name} has been modified successfully.`,
          );
          fetchData();
          setModalOpen(false);
        }
      } catch (err) {
        toast.error("Update failed");
      }
    };

    return (
      <div className="fixed inset-0 bg-black/65 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ y: 50 }}
          animate={{ y: 0 }}
          className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        >
          <div className="p-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-black text-gray-800 uppercase tracking-tighter">
                Edit Profile
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 bg-gray-100 rounded-full"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="w-32 h-32 rounded-[2rem] overflow-hidden border-4 border-pink-100">
                    {preview ? (
                      <img
                        src={preview}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FaUserCircle size={128} className="text-gray-100" />
                    )}
                  </div>
                  <label className="absolute -bottom-2 -right-2 bg-pink-500 text-white p-3 rounded-2xl cursor-pointer shadow-xl">
                    <FaCamera />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setImgFile(file);
                        setPreview(URL.createObjectURL(file));
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                 <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                 Full Name
                </label>
                <input
                  type="text"
                  placeholder="Full Name"
                  className="w-full p-4 bg-gray-50 rounded-2xl border-none font-bold"
                  value={formData.user_name}
                  onChange={(e) =>
                    setFormData({ ...formData, user_name: e.target.value })
                  }
                  required
                />
                </div>
                 <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
                </label>
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full p-4 bg-gray-50 rounded-2xl border-none font-bold"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
                </div>
                 <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                   Mobile Number
                </label>
                <input
                  type="tel"
                  placeholder="Mobile"
                  className="w-full p-4 bg-gray-50 rounded-2xl border-none font-bold"
                  value={formData.mobile_number}
                  onChange={(e) =>
                    setFormData({ ...formData, mobile_number: e.target.value })
                  }
                  required
                />
                </div>
                  <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alternate Mobile
                </label>
                <input
                  type="tel"
                  name="alternate_mobile_number"
                  value={formData.alternate_mobile_number}
                  onChange={(e) =>
                    setFormData({ ...formData, alternate_mobile_number: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition"
                />
              </div>
               <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition bg-white"
                  value={formData.gender}
                  onChange={(e) =>
                    setFormData({ ...formData, gender: e.target.value })
                  }
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
</div>
                 <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition"
                />
              </div>
              
              </div>
    <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition resize-none"
              />
            </div>
              <div className="flex items-center gap-4 p-4 bg-pink-50 rounded-2xl">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  className="w-6 h-6 accent-pink-500"
                />
                <span className="font-black text-xs uppercase tracking-widest text-pink-700">
                  Account Active
                </span>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-5 rounded-2xl font-black uppercase tracking-widest text-gray-400"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="flex-1 py-5 bg-pink-500 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-pink-100"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Toaster />
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 pb-12">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 px-4 sm:px-6 lg:px-8 py-10">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-pink-500 text-white rounded-2xl shadow-lg shadow-pink-100">
                <UserCheck size={32} />
              </div>
              <h1 className="text-4xl font-black text-gray-800 uppercase tracking-tighter">
                Customers
              </h1>
            </div>
            <p className="text-gray-400 font-bold uppercase text-[10px] tracking-[0.3em] ml-16">
              Management Portal
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-4 w-full lg:w-auto">
            {/* Organization Dropdown */}
            <div className="relative group min-w-[240px]">
              <FaBuilding className="absolute left-5 top-1/2 -translate-y-1/2 text-pink-500 group-focus-within:scale-110 transition-transform" />
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="w-full pl-12 pr-10 py-5 bg-white border-2 border-transparent focus:border-pink-500 rounded-[1.5rem] shadow-xl shadow-gray-100 outline-none appearance-none font-bold text-gray-700 cursor-pointer text-sm"
              >
                <option value="">All Organizations</option>
                {organizations.map((org) => (
                  <option key={org.organization_id} value={org.organization_id}>
                    {org.organization_name}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none"
                size={16}
              />
            </div>

            {/* Search Bar */}
            <div className="relative flex-1 md:min-w-[400px]">
              <FaSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300" />
              <input
                type="search"
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-14 pr-6 py-5 bg-white border-2 border-transparent focus:border-pink-500 rounded-[1.5rem] shadow-xl shadow-gray-100 outline-none font-bold text-sm"
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(itemsPerPage)].map((_, i) => (
              <div
                key={i}
                className="h-96 bg-white rounded-[2.5rem] animate-pulse border border-gray-100"
              />
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-40 text-center">
            <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaUserCircle size={64} className="text-gray-300" />
            </div>
            <h2 className="text-2xl font-black text-gray-400 uppercase tracking-widest">
              No matching records
            </h2>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              <AnimatePresence mode="popLayout">
                {currentTableData.map((user) => (
                  <CustomerCard key={user.user_id} user={user} />
                ))}
              </AnimatePresence>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-16 flex items-center justify-center gap-2">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  className="p-4 rounded-2xl bg-white shadow-lg text-gray-400 disabled:opacity-30 hover:text-pink-500 transition-colors"
                >
                  <FaChevronLeft />
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-12 h-12 rounded-2xl font-black transition-all ${
                      currentPage === i + 1
                        ? "bg-pink-500 text-white shadow-lg shadow-pink-200 scale-110"
                        : "bg-white text-gray-400 hover:bg-pink-50"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  className="p-4 rounded-2xl bg-white shadow-lg text-gray-400 disabled:opacity-30 hover:text-pink-500 transition-colors"
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
            <p className="text-center mt-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">
              Showing {currentTableData.length} of {filteredUsers.length} total
              users
            </p>
          </>
        )}
      </div>

      {modalOpen && <EditCustomerModal />}
    </div>
  );
};

export default AllOrganizationCustomers;
